import { Grid, usePageWidth } from '@fs/zion-ui'
import React from 'react'
import { useAtPageContentSize } from '../PageContentWidthContext'
import FamilyMembersCardSkeleton from '../cards/family-members-card/FamilyMembersCardSkeleton'
import VitalsCardSkeleton from '../cards/vitals-card/VitalsCardSkeleton'
import OtherInformationCardSkeleton from '../cards/other-information-card/OtherInformationCardSkeleton'
import OtherRelationshipsCardSkeleton from '../cards/other-relationships-card/OtherRelationshipsCardSkeleton'
import LifeSketchCardSkeleton from '../cards/life-sketch-card/LifeSketchCardSkeleton'
import ResearchHelpCardSkeleton from '../cards/research-help-card/ResearchHelpCardSkeleton'
import SearchRecordsCardSkeleton from '../cards/search-records-card/SearchRecordsCardSkeleton'
import MiniNotesCardSkeleton from '../cards/notes-card/MiniNotesCardSkeleton'
import LatestChangesCardSkeleton from '../cards/latest-changes-card/LatestChangesCardSkeleton'
import ToolsCardSkeleton from '../cards/tools-card/ToolsCardSkeleton'
import preferenceKeys from '../preference-keys'
import { defaultSectionOrder } from '../../providers/SectionOrderProvider'
import { localStorageGetItem } from '../../utils/localStorage'

function getSectionOrderFromStorage(): string[] {
  const raw = localStorageGetItem(preferenceKeys.SECTION_ORDER)
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    } catch {
      // ignore malformed JSON
    }
  }
  return defaultSectionOrder
}

export default function DetailsTabSkeleton(): JSX.Element {
  const pageWidth = usePageWidth()
  const atWidth = useAtPageContentSize()
  const showSecondaryCards = pageWidth({ default: false, lg: true })
  const moveSecondaryCards = atWidth({ default: showSecondaryCards, lg: false })
  const gridGutters = 'nano'
  const secondaryCardsColumns = (() => {
    if (moveSecondaryCards) return 12
    if (showSecondaryCards) return 4
    return 'hide'
  })()
  const primaryCardsColumns = moveSecondaryCards || !showSecondaryCards ? 12 : 8

  const sectionOrder = getSectionOrderFromStorage()

  const sectionSkeletonMap: Record<string, JSX.Element> = {
    vitals: <VitalsCardSkeleton key="vitals" totalColumns={primaryCardsColumns} isSingleCardPage={false} />,
    'other-information': (
      <OtherInformationCardSkeleton
        key="other-information"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
    'family-members': (
      <FamilyMembersCardSkeleton key="family-members" totalColumns={primaryCardsColumns} isSingleCardPage={false} />
    ),
    'other-relationships': (
      <OtherRelationshipsCardSkeleton
        key="other-relationships"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
    lifesketch: <LifeSketchCardSkeleton key="lifesketch" isSingleCardPage={false} />,
  }

  return (
    <Grid gutters="nano">
      <Grid gutters={gridGutters}>
        <Grid gutters={gridGutters} columns={primaryCardsColumns} data-testid="left-column">
          {sectionOrder.map((sectionId) => sectionSkeletonMap[sectionId]).filter(Boolean)}
        </Grid>

        <Grid gutters={gridGutters} columns={secondaryCardsColumns} data-testid="right-column">
          <ResearchHelpCardSkeleton isSingleCardPage={false} />
          <SearchRecordsCardSkeleton isSingleCardPage={false} />
          <MiniNotesCardSkeleton isSingleCardPage={false} />
          <LatestChangesCardSkeleton />
          <ToolsCardSkeleton isSingleCardPage={false} />
        </Grid>
      </Grid>
    </Grid>
  )
}
